import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, FileText, User, Users } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGeneralStats } from "@/lib/queries/analytics";

export const Route = createFileRoute("/_app/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: stats, isLoading, error } = useGeneralStats();

  if (isLoading) {
    return (
      <div className="w-full p-6">
        <h1 className="mb-6 text-3xl font-bold">Dashboard</h1>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-full">
              <CardHeader>
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <h1 className="mb-6 text-3xl font-bold">Dashboard</h1>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error Loading Analytics</CardTitle>
            <CardDescription className="text-destructive">
              {error.message}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const statsCards = [
    {
      title: "Users",
      value: stats?.total_users ?? 0,
      description: "Active users in the system",
      icon: User,
      href: "/users",
    },
    {
      title: "Groups",
      value: stats?.total_groups ?? 0,
      description: "User groups configured",
      icon: Users,
      href: "/groups",
    },
    {
      title: "Policies",
      value: stats?.total_policies ?? 0,
      description: "Active security policies",
      icon: FileText,
      href: "/policies",
    },
    {
      title: "Incidents in This Week",
      shortTitle: "Incidents",
      value: stats?.incidents_last_week ?? 0,
      description: "Security incidents reported",
      icon: AlertTriangle,
      href: "/incidents",
    },
  ];

  return (
    <div className="w-full p-6">
      <h1 className="mb-6 text-3xl font-bold">Dashboard</h1>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.href} className="group block h-full">
              <Card className="relative h-full cursor-pointer overflow-hidden border transition-all hover:border-[#FCCEEE] hover:bg-[#FDF2FA] hover:shadow-lg dark:hover:bg-[#FDF2FA]/10">
                <Icon className="pointer-events-none absolute -top-3 -right-9 h-20 w-20 text-[#FAFAFA] dark:text-white/6" />
                <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] transition-colors select-none group-hover:text-primary/15 dark:text-white/6 dark:group-hover:text-primary/15">
                  {stat.shortTitle ?? stat.title}
                </span>
                <CardHeader className="relative z-10 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-[#DD2590]">
                      {stat.value}
                    </div>
                    <div className="rounded-lg p-2">
                      <Icon className="h-10 w-10 text-[#535862] transition-colors group-hover:text-primary dark:text-white dark:group-hover:text-primary" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-lg font-bold text-[#414651] transition-colors group-hover:text-[#DD2590] dark:text-white dark:group-hover:text-primary">
                    {stat.title}
                  </p>
                  <p className="mt-1 text-md text-[#717680] transition-colors group-hover:text-[#EE46BC] dark:text-white dark:group-hover:text-[#EE46BC]">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
